import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  Check,
  Code,
  Cpu,
  Database,
  ExternalLink,
  FileText,
  LayoutDashboard as Layout,
  Mail,
  Play,
  Radio,
  Share2,
  Terminal,
  Zap
} from 'lucide-react';
import styled from '../../styled';
import { usePrefersReducedMotion } from '../motion';

const ACCENT = '#1890ff';
const INK = '#0e1c2b';

type NetworkTile = {
  src: string;
  alt: string;
  x: number; // percent of illustration width
  y: number; // px offset from top of the cloud
  r: number; // scatter rotation in degrees
};

// Two slightly uneven semicircular orbits keep the network side feeling organic.
const NETWORKS: NetworkTile[] = [
  { src: '/images/icons/cj.svg', alt: 'CJ', x: 12, y: 177, r: -8 },
  { src: '/images/logos/impact-logo.svg', alt: 'Impact', x: 20, y: 92, r: -5 },
  { src: '/images/logos/amazon-logo.svg', alt: 'Amazon', x: 34, y: 28, r: -3 },
  { src: '/images/icons/skimlinks.jpeg', alt: 'Skimlinks', x: 50, y: 3, r: 1 },
  { src: '/images/logos/partnerize.png', alt: 'Partnerize', x: 66, y: 28, r: 3 },
  { src: '/images/logos/tradedoubler.png', alt: 'Tradedoubler', x: 80, y: 92, r: 6 },
  { src: '/images/logos/shareasale.svg', alt: 'ShareASale', x: 88, y: 177, r: 8 },

  { src: '/images/icons/awin.png', alt: 'Awin', x: 28, y: 181, r: -7 },
  { src: '/images/icons/rakuten.png', alt: 'Rakuten', x: 36, y: 119, r: -4 },
  { src: '/images/icons/linkby.ico', alt: 'Linkby', x: 45, y: 96, r: -1 },
  { src: '/images/logos/ltk.jpeg', alt: 'LTK', x: 56, y: 89, r: 2 },
  { src: '/images/logos/ebay-partner-network.png', alt: 'eBay', x: 65, y: 127, r: 5 }
];

const SCENES = [
  {
    step: 'Unify',
    title: 'Affiliate data from 100+ networks, in one place',
    subtitle:
      'Bring transactions, rates, finance data, and payouts into one trusted source of truth.'
  },
  {
    step: 'Optimize',
    title: 'See what every single placement earns',
    subtitle:
      'Connect revenue to every link and placement across content, email, syndication, and social.'
  },
  {
    step: 'Amplify',
    title: 'Execute campaigns end-to-end with AI-powered suggestions',
    subtitle:
      'Turn live performance data into campaigns and revenue opportunities your team can review and launch.'
  }
] as const;

const STEP_COUNT = SCENES.length;
const SCENE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0, 0.24],
  [0.24, 0.7],
  [0.7, 1]
];

// Geometry of the connector SVG (viewBox units)
const CLOUD_W = 700;
const CLOUD_H = 300;
const FOCUS_X = CLOUD_W / 2;
const FOCUS_Y = CLOUD_H - 4;
const TILE = 56;

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

const useStickyScrollProgress = (
  stageRef: React.RefObject<HTMLDivElement>,
  enabled: boolean
) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const stage = stageRef.current;
    if (!stage || typeof window === 'undefined') {
      return;
    }

    const update = () => {
      const rect = stage.getBoundingClientRect();
      const scrollable = stage.offsetHeight - window.innerHeight;
      if (scrollable <= 0) {
        setProgress(1);
        return;
      }
      setProgress(clamp01(-rect.top / scrollable));
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [stageRef, enabled]);

  return progress;
};

const ScrollStage = styled('div')<{ $reduced: boolean }>`
  height: ${(p) => (p.$reduced ? 'auto' : '300vh')};
  position: relative;
`;

const StickyViewport = styled('div')<{ $reduced: boolean }>`
  position: ${(p) => (p.$reduced ? 'relative' : 'sticky')};
  top: ${(p) => (p.$reduced ? 'auto' : '8vh')};
  height: ${(p) => (p.$reduced ? 'auto' : '84vh')};
  min-height: ${(p) => (p.$reduced ? '0' : '600px')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StepCounter = styled('div')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${ACCENT};
  text-align: center;
  margin-bottom: 12px;
`;

const SceneTitles = styled('div')`
  position: relative;
  height: 86px;
  width: 100%;
  margin-bottom: 18px;

  @media (max-width: 600px) {
    height: 96px;
    margin-bottom: 12px;
  }
`;

const SceneCopy = styled('div')<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  text-align: center;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? 0 : 8)}px);
  transition: opacity 0.45s ease, transform 0.45s ease;
`;

const SceneTitle = styled('h3')`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: ${INK};
  line-height: 1.3;
  padding: 0 24px;

  @media (max-width: 600px) {
    font-size: 19px;
  }
`;

const SceneSubtitle = styled('p')`
  max-width: 650px;
  margin: 8px auto 0;
  padding: 0 24px;
  font-size: 15px;
  line-height: 1.5;
  color: #64748b;

  @media (max-width: 600px) {
    margin-top: 6px;
    font-size: 13px;
    line-height: 1.4;
  }
`;

const SceneStage = styled('div')`
  position: relative;
  width: 100%;
  max-width: 760px;
  height: 520px;
  margin: 0 auto;
  padding: 0 16px;

  @media (max-width: 600px) {
    height: 440px;
  }
`;

/* Soft blue spotlight behind the active illustration */
const StageGlow = styled('div')`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 720px;
  height: 480px;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    ellipse at center,
    rgba(24, 144, 255, 0.09) 0%,
    rgba(24, 144, 255, 0.03) 45%,
    transparent 70%
  );
  pointer-events: none;

  @media (min-width: 961px) {
    width: 830px;
    height: 550px;
  }
`;

const SceneLayer = styled('div')<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};
  transition: opacity 0.5s ease;

  /* Enlarge the mockups on desktop; the illustrations are built on
     fluid containers, so a uniform scale keeps every scene in sync. */
  @media (min-width: 961px) {
    transform: scale(1.15);
  }
`;

/* ------------------------- Scene 1: Unify ------------------------- */

const Cloud = styled('div')`
  position: relative;
  height: ${CLOUD_H}px;

  @media (max-width: 600px) {
    height: 230px;
  }
`;

const ConnectorSvg = styled('svg')<{ $drawnIn: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* Shrinking a circle centered on the hub retracts every line into it */
  clip-path: circle(${(p) => (p.$drawnIn ? '0%' : '140%')} at 50% 99%);
  transition: clip-path 0.85s cubic-bezier(0.22, 1, 0.36, 1);

  @keyframes connectorFlow {
    from {
      stroke-dashoffset: 0;
    }
    to {
      /* One full dash period (3 + 6) so the loop is seamless */
      stroke-dashoffset: -9;
    }
  }

  path {
    fill: none;
    stroke: rgba(14, 28, 43, 0.18);
    stroke-width: 1;
    stroke-dasharray: 3 6;
    animation: connectorFlow 1.4s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
`;

const Tile = styled('div')<{
  $x: number;
  $y: number;
  $r: number;
  $converged: boolean;
}>`
  position: absolute;
  left: ${(p) => (p.$converged ? 50 : p.$x)}%;
  top: ${(p) => (p.$converged ? CLOUD_H - 30 : p.$y)}px;
  width: ${TILE}px;
  margin-left: ${-TILE / 2}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  transform: rotate(${(p) => (p.$converged ? 0 : p.$r)}deg)
    scale(${(p) => (p.$converged ? 0.2 : 1)});
  opacity: ${(p) => (p.$converged ? 0 : 1)};
  transition: left 0.8s cubic-bezier(0.22, 1, 0.36, 1),
    top 0.8s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.8s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s ease 0.15s;

  @media (max-width: 600px) {
    transform: rotate(${(p) => (p.$converged ? 0 : p.$r)}deg)
      scale(${(p) => (p.$converged ? 0.2 : 0.72)});
    top: ${(p) => (p.$converged ? 130 : p.$y * 0.75)}px;
  }
`;

const TileIcon = styled('div')`
  width: ${TILE}px;
  height: ${TILE}px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 8px 20px rgba(14, 28, 43, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const MORE_TILE: Pick<NetworkTile, 'x' | 'y' | 'r'> = { x: 73, y: 174, r: 7 };

const MoreTileBox = styled('div')`
  width: ${TILE}px;
  height: ${TILE}px;
  border-radius: 14px;
  border: 1.5px dashed #94a3b8;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
`;

const HubBadge = styled('div')<{ $lit: boolean }>`
  position: relative;
  z-index: 2;
  width: 48px;
  height: 48px;
  margin: -52px auto 0;
  border-radius: 13px;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: ${(p) =>
    p.$lit
      ? '0 0 0 6px rgba(24, 144, 255, 0.14), 0 8px 30px rgba(24, 144, 255, 0.35)'
      : '0 6px 18px rgba(14, 28, 43, 0.12)'};
  transition: box-shadow 0.6s ease;

  img {
    /* The asset carries its own padding, so let it fill the badge */
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

/* Fan-out: mirror of the top funnel, from the badge to the preview cards */
const FAN_W = 640;
const FAN_H = 52;
const FAN_TARGETS = [60, 190, 320, 450, 580];

const FanSvg = styled('svg')<{ $drawnOut: boolean }>`
  display: block;
  width: 100%;
  max-width: ${FAN_W}px;
  height: ${FAN_H}px;
  margin: -6px auto 0;
  overflow: visible;
  /* Growing a circle centered on the badge draws every line outward */
  clip-path: circle(${(p) => (p.$drawnOut ? '150%' : '0%')} at 50% 0%);
  transition: clip-path 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.25s;

  @keyframes fanFlow {
    from {
      stroke-dashoffset: 0;
    }
    to {
      stroke-dashoffset: -9;
    }
  }

  path {
    fill: none;
    stroke: rgba(24, 144, 255, 0.45);
    stroke-width: 1.2;
    stroke-dasharray: 3 6;
    animation: fanFlow 1.4s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

const PreviewGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  width: 100%;
  max-width: ${FAN_W}px;
  margin: 0 auto;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    margin-top: 14px;

    & > :last-child {
      grid-column: 1 / -1;
      width: calc(50% - 5px);
      justify-self: center;
      box-sizing: border-box;
    }
  }
`;

const Destination = styled('div')<{ $lit: boolean; $delay: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 84px;
  opacity: ${(p) => (p.$lit ? 1 : 0)};
  transform: translateY(${(p) => (p.$lit ? 0 : 18)}px)
    scale(${(p) => (p.$lit ? 1 : 0.97)});
  transition: opacity 0.5s ease ${(p) => p.$delay}ms,
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${(p) => p.$delay}ms;
`;

const DestinationMark = styled('div')<{ $lit: boolean }>`
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: linear-gradient(145deg, #fff 20%, #edf7ff 100%);
  border: 1px solid
    ${(p) => (p.$lit ? 'rgba(24, 144, 255, 0.5)' : '#dbe7f2')};
  box-shadow: ${(p) =>
    p.$lit
      ? '0 0 0 5px rgba(24, 144, 255, 0.08), 0 12px 30px rgba(24, 144, 255, 0.18)'
      : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ACCENT};
  transition: border-color 0.5s ease, box-shadow 0.5s ease;

  &::after {
    content: '';
    position: absolute;
    inset: 5px;
    border-radius: 11px;
    border: 1px solid rgba(24, 144, 255, 0.08);
    pointer-events: none;
  }
`;

const DestinationLabel = styled('div')`
  font-size: 12.5px;
  font-weight: 700;
  color: ${INK};
  line-height: 1.3;
  text-align: center;
  white-space: nowrap;
`;

const DESTINATIONS = [
  {
    icon: Layout,
    label: 'Affilimate App'
  },
  {
    icon: Cpu,
    label: 'Affilimate Agent'
  },
  {
    icon: Terminal,
    label: 'MCP'
  },
  {
    icon: Code,
    label: 'API'
  },
  {
    icon: Database,
    label: 'Data warehouse'
  }
] as const;

const connectorPath = (tile: NetworkTile) => {
  const cx = (tile.x / 100) * CLOUD_W;
  const cy = tile.y + TILE;
  return `M ${cx} ${cy} C ${cx} ${cy + 50}, ${FOCUS_X} ${FOCUS_Y - 55}, ${FOCUS_X} ${FOCUS_Y}`;
};

/* ------------------------ Scene 2: Optimize ----------------------- */

const CHANNELS = [
  { label: 'Content', icon: FileText },
  { label: 'Email', icon: Mail },
  { label: 'Syndication', icon: Radio },
  { label: 'Social', icon: Share2 }
] as const;

const ChannelRail = styled('div')`
  width: 100%;
  max-width: 560px;
  margin: 0 auto 14px;
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 7px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(14, 28, 43, 0.06);
  box-sizing: border-box;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    margin-bottom: 10px;
    padding: 6px;
  }
`;

const Channel = styled('div')<{ $lit: boolean; $delay: number }>`
  min-height: 44px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid
    ${(p) => (p.$lit ? 'rgba(24, 144, 255, 0.42)' : '#edf2f7')};
  border-radius: 11px;
  background: ${(p) => (p.$lit ? '#f0f8ff' : 'rgba(248, 250, 252, 0.9)')};
  color: ${(p) => (p.$lit ? INK : '#64748b')};
  font-size: 11.5px;
  font-weight: 700;
  box-shadow: ${(p) =>
    p.$lit ? '0 6px 18px rgba(24, 144, 255, 0.1)' : 'none'};
  transform: translateY(${(p) => (p.$lit ? 0 : 3)}px);
  transition: color 0.4s ease ${(p) => p.$delay}ms,
    border-color 0.4s ease ${(p) => p.$delay}ms,
    background 0.4s ease ${(p) => p.$delay}ms,
    box-shadow 0.4s ease ${(p) => p.$delay}ms,
    transform 0.4s ease ${(p) => p.$delay}ms;

  svg {
    flex-shrink: 0;
    color: ${(p) => (p.$lit ? ACCENT : '#94a3b8')};
    transition: color 0.4s ease ${(p) => p.$delay}ms;
  }
`;

const heatProducts = [
  {
    name: 'Breville Barista Express',
    badge: 'Best overall',
    bubble: { size: 92, color: 'rgba(240, 82, 82, 0.55)', ring: true },
    stats: true
  },
  {
    name: 'Gaggia Classic Pro',
    badge: 'Best for beginners',
    bubble: { size: 64, color: 'rgba(244, 143, 177, 0.5)', ring: false },
    stats: false
  },
  {
    name: 'De’Longhi Dedica',
    badge: 'Budget pick',
    bubble: { size: 42, color: 'rgba(56, 169, 255, 0.5)', ring: false },
    stats: false
  }
];

const ArticleCard = styled('div')`
  position: relative;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 22px 24px 60px;
  box-shadow: 0 24px 64px rgba(14, 28, 43, 0.14);
`;

const ArticleTitle = styled('div')`
  font-family: 'Merriweather', serif;
  font-size: 17px;
  font-weight: 700;
  color: #0e1c2b;
  margin-bottom: 6px;
`;

const ArticleByline = styled('div')`
  font-size: 10px;
  color: #94a3b8;
  margin-bottom: 16px;
`;

const ProductRow = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 0;
  border-top: 1px solid #f1f5f9;
`;

const ProductThumb = styled('div')`
  width: 52px;
  height: 40px;
  border-radius: 6px;
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  flex-shrink: 0;
`;

const ProductInfo = styled('div')`
  flex: 1;
  min-width: 0;
`;

const ProductName = styled('div')`
  font-size: 12px;
  font-weight: 700;
  color: #0e1c2b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProductBadge = styled('span')`
  display: inline-block;
  font-size: 8px;
  font-weight: 700;
  color: #fff;
  background: #334155;
  border-radius: 3px;
  padding: 2px 6px;
  margin-top: 4px;
`;

const CheckButton = styled('div')`
  position: relative;
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: #334155;
  border-radius: 5px;
  padding: 8px 14px;
  white-space: nowrap;
`;

const HeatBubble = styled('div')<{
  $size: number;
  $color: string;
  $selected: boolean;
  $visible: boolean;
}>`
  position: absolute;
  /* Center the bubble over the Check price button (~68px wide, at the row's right edge) */
  right: ${(p) => 34 - p.$size / 2}px;
  top: 50%;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  border: ${(p) =>
    p.$selected ? '2px solid #334155' : '2px solid transparent'};
  transform: translateY(-50%) scale(${(p) => (p.$visible ? 1 : 0)});
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: transform 0.55s cubic-bezier(0.34, 1.56, 0.64, 1),
    opacity 0.4s ease, border-color 0.3s ease;
  pointer-events: none;
  z-index: 1;

  @keyframes bubbleBreathe {
    0%,
    100% {
      transform: translateY(-50%) scale(1);
    }
    50% {
      transform: translateY(-50%) scale(1.08);
    }
  }

  /* Gentle breathing once the pop-in transition has finished. The 0.7s
     delay keeps the animation from overriding the entrance transform. */
  ${(p) =>
    p.$visible &&
    'animation: bubbleBreathe 3.2s ease-in-out 0.7s infinite;'}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const StatsBar = styled('div')<{ $visible: boolean }>`
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 14px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: #0e1c2b;
  border-radius: 100px;
  padding: 10px 18px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  transform: translateY(${(p) => (p.$visible ? 0 : 24)}px);
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.4s ease;
  overflow: hidden;
`;

const StatsLink = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;

  svg {
    opacity: 0.7;
  }
`;

const StatItem = styled('span')<{ $highlight?: 'red' | 'green' }>`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
  white-space: nowrap;

  strong {
    color: #fff;
    font-weight: 700;
    ${(p) =>
      p.$highlight === 'red' &&
      'background: #b03a3a; border-radius: 4px; padding: 2px 6px;'}
    ${(p) =>
      p.$highlight === 'green' &&
      'background: #b7eb8f; color: #135200; border-radius: 4px; padding: 2px 6px;'}
  }

  @media (max-width: 600px) {
    &[data-optional] {
      display: none;
    }
  }
`;

const OptimizeDemoStage = styled('div')`
  position: relative;
  width: 100%;
  max-width: 560px;
  height: 270px;
  margin: 0 auto;

  @media (max-width: 600px) {
    height: 300px;
  }
`;

const OptimizeDemoLayer = styled('div')<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${(p) => (p.$active ? 1 : 0)};
  transform: translateY(${(p) => (p.$active ? 0 : 8)}px);
  pointer-events: ${(p) => (p.$active ? 'auto' : 'none')};
  transition: opacity 0.4s ease, transform 0.4s ease;
`;

const OptimizeReportCard = styled('div')`
  width: 100%;
  min-height: 230px;
  padding: 20px 22px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 24px 64px rgba(14, 28, 43, 0.14);
`;

const ReportHeader = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid #eef2f7;
`;

const ReportTitle = styled('div')`
  font-size: 15px;
  font-weight: 800;
  color: ${INK};
`;

const ReportSubtitle = styled('div')`
  margin-top: 4px;
  font-size: 10px;
  color: #94a3b8;
`;

const ReportTotal = styled('div')`
  flex-shrink: 0;
  text-align: right;
  font-size: 19px;
  font-weight: 800;
  color: ${INK};

  span {
    display: block;
    margin-top: 3px;
    font-size: 9px;
    font-weight: 600;
    color: #64748b;
  }
`;

const EmailRows = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 13px;
  margin-top: 15px;
`;

const EmailRow = styled('div')`
  display: grid;
  grid-template-columns: 140px 1fr 62px;
  align-items: center;
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr auto;
    gap: 5px 10px;
  }
`;

const EmailSection = styled('div')`
  min-width: 0;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-size: 11px;
    color: ${INK};
  }

  span {
    margin-top: 2px;
    font-size: 9px;
    color: #64748b;
  }
`;

const EmailBarTrack = styled('div')`
  height: 8px;
  overflow: hidden;
  border-radius: 100px;
  background: #eef2f7;

  @media (max-width: 600px) {
    grid-column: 1 / -1;
    grid-row: 2;
  }
`;

const EmailBar = styled('div')<{ $width: number }>`
  width: ${(p) => p.$width}%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #1890ff, #69c0ff);
`;

const EmailAmount = styled('div')`
  text-align: right;
  font-size: 11px;
  font-weight: 800;
  color: ${INK};
`;

const PlatformGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 22px;
`;

const Platform = styled('div')<{ $color: string }>`
  min-height: 74px;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  font-size: 11px;
  font-weight: 800;
  color: ${INK};

  &::before {
    content: '';
    width: 22px;
    height: 4px;
    border-radius: 100px;
    background: ${(p) => p.$color};
  }
`;

const SyndicationNote = styled('div')`
  margin-top: 16px;
  text-align: center;
  font-size: 11px;
  line-height: 1.45;
  color: #64748b;
`;

const VideoSummary = styled('div')`
  display: grid;
  grid-template-columns: 145px 1fr;
  gap: 16px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eef2f7;

  @media (max-width: 600px) {
    grid-template-columns: 105px 1fr;
    gap: 11px;
  }
`;

const VideoThumbnail = styled('div')`
  height: 82px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: linear-gradient(135deg, #1e293b, #475569);
`;

const PlayBadge = styled('div')`
  width: 36px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: #ff0033;
  color: #fff;
`;

const VideoDetails = styled('div')`
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;

  strong {
    font-size: 13px;
    line-height: 1.35;
    color: ${INK};
  }

  span {
    margin-top: 5px;
    font-size: 9px;
    color: #94a3b8;
  }
`;

const Shortlink = styled('div')`
  margin-top: 8px;
  padding: 6px 8px;
  overflow: hidden;
  border: 1px solid #dbeafe;
  border-radius: 6px;
  background: #eff8ff;
  color: ${ACCENT};
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 10px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const CommissionReport = styled('div')`
  display: grid;
  grid-template-columns: 150px 1fr;
  align-items: end;
  gap: 18px;
  margin-top: 15px;

  @media (max-width: 600px) {
    grid-template-columns: 115px 1fr;
    gap: 10px;
  }
`;

const CommissionValue = styled('div')`
  font-size: 20px;
  font-weight: 800;
  color: ${INK};

  span {
    display: block;
    margin-bottom: 4px;
    font-size: 9px;
    font-weight: 700;
    color: #64748b;
  }
`;

const CommissionBars = styled('div')`
  height: 58px;
  display: flex;
  align-items: flex-end;
  gap: 7px;
  padding-bottom: 3px;
  border-bottom: 1px solid #cbd5e1;
`;

const CommissionBar = styled('div')<{ $height: number }>`
  flex: 1;
  height: ${(p) => p.$height}%;
  min-width: 5px;
  border-radius: 3px 3px 0 0;
  background: linear-gradient(180deg, #69c0ff, #1890ff);
`;

/* ------------------------ Scene 3: Amplify ------------------------ */

const AmplifyFlow = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 12px;
  align-items: stretch;
  width: 100%;
  max-width: 620px;
  margin: 0 auto;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    max-width: 280px;
  }
`;

const FlowArrow = styled('div')<{ $lit: boolean }>`
  display: flex;
  align-items: center;
  color: ${(p) => (p.$lit ? ACCENT : '#cbd5e1')};
  transition: color 0.5s ease;

  @media (max-width: 600px) {
    justify-content: center;
    transform: rotate(90deg);
  }
`;

const AmplifyCard = styled('div')<{ $active: boolean; $complete?: boolean }>`
  min-height: 170px;
  padding: 20px 18px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
  border: 1px solid
    ${(p) => (p.$active ? 'rgba(24, 144, 255, 0.48)' : '#e2e8f0')};
  border-radius: 14px;
  background: #fff;
  box-shadow: ${(p) =>
    p.$active
      ? '0 16px 40px rgba(24, 144, 255, 0.15)'
      : '0 8px 24px rgba(14, 28, 43, 0.05)'};
  opacity: ${(p) => (p.$active || p.$complete ? 1 : 0.55)};
  transform: translateY(${(p) => (p.$active ? 0 : 5)}px);
  transition: border-color 0.5s ease, box-shadow 0.5s ease,
    opacity 0.5s ease, transform 0.5s ease;
`;

const FlowKicker = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;

  svg {
    color: ${ACCENT};
  }
`;

const FlowValue = styled('div')`
  margin-top: 10px;
  font-size: 20px;
  font-weight: 800;
  color: ${INK};
  line-height: 1.2;
`;

const FlowCaption = styled('div')`
  margin-top: 7px;
  font-size: 11px;
  color: #64748b;
  line-height: 1.45;
`;

const FlowMeta = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;

  span {
    padding: 4px 7px;
    border-radius: 100px;
    background: #f1f5f9;
    font-size: 9px;
    font-weight: 600;
    color: #475569;
  }
`;

const ReviewPill = styled('div')`
  align-self: flex-start;
  margin-top: 12px;
  padding: 6px 10px;
  border-radius: 100px;
  background: #eff8ff;
  color: #096dd9;
  font-size: 10px;
  font-weight: 700;
`;

/* ------------------------------ Panel ----------------------------- */

export const CommerceLoopPanel = () => {
  const reduced = usePrefersReducedMotion();
  const stageRef = useRef<HTMLDivElement>(null);
  const progress = useStickyScrollProgress(stageRef, !reduced);

  const step = reduced
    ? STEP_COUNT - 1
    : progress < SCENE_RANGES[0][1]
      ? 0
      : progress < SCENE_RANGES[1][1]
        ? 1
        : 2;

  const local = (index: number) => {
    if (reduced) {
      return 1;
    }
    const range = SCENE_RANGES[index];
    return range ? clamp01((progress - range[0]) / (range[1] - range[0])) : 0;
  };

  const converged = local(0) > 0.45;
  const fannedOut = reduced || local(0) > 0.55;
  const optimizeProgress = local(1);
  const activeChannel =
    optimizeProgress < 0.4
      ? 0
      : optimizeProgress < 0.6
        ? 1
        : optimizeProgress < 0.8
          ? 2
          : 3;
  const contentFocused = step === 1 && activeChannel === 0;
  const bubblesVisible = contentFocused && optimizeProgress > 0.05;
  const placementSelected = contentFocused && optimizeProgress > 0.1;
  const statsVisible = contentFocused && optimizeProgress > 0.16;
  const reviewReady = reduced || (step === 2 && local(2) > 0.48);

  const scene = SCENES[step];

  return (
    <ScrollStage ref={stageRef} $reduced={reduced}>
      <StickyViewport $reduced={reduced}>
        <StepCounter aria-hidden>
          {scene.step}
        </StepCounter>

        <SceneTitles>
          {SCENES.map((s, index) => (
            <SceneCopy key={s.step} $visible={index === step}>
              <SceneTitle>{s.title}</SceneTitle>
              <SceneSubtitle>{s.subtitle}</SceneSubtitle>
            </SceneCopy>
          ))}
        </SceneTitles>

        <SceneStage>
          <StageGlow aria-hidden />
          {/* Scene 1: Unify */}
          <SceneLayer $visible={step === 0}>
            <Cloud>
              <ConnectorSvg
                viewBox={`0 0 ${CLOUD_W} ${CLOUD_H}`}
                preserveAspectRatio="none"
                aria-hidden
                $drawnIn={converged && !reduced}
              >
                {NETWORKS.map((network) => (
                  <path key={network.alt} d={connectorPath(network)} />
                ))}
                <path d={connectorPath(MORE_TILE as NetworkTile)} />
              </ConnectorSvg>
              {NETWORKS.map((network) => (
                <Tile
                  key={network.alt}
                  $x={network.x}
                  $y={network.y}
                  $r={network.r}
                  $converged={converged && !reduced}
                >
                  <TileIcon>
                    <img src={network.src} alt={network.alt} />
                  </TileIcon>
                </Tile>
              ))}
              <Tile
                $x={MORE_TILE.x}
                $y={MORE_TILE.y}
                $r={MORE_TILE.r}
                $converged={converged && !reduced}
              >
                <MoreTileBox>+100</MoreTileBox>
              </Tile>
            </Cloud>

            <HubBadge $lit={converged}>
              <img src="/images/icon-white-padded.png" alt="Affilimate" />
            </HubBadge>

            <FanSvg
              viewBox={`0 0 ${FAN_W} ${FAN_H}`}
              preserveAspectRatio="none"
              aria-hidden
              $drawnOut={fannedOut}
            >
              {FAN_TARGETS.map((x) => (
                <path
                  key={x}
                  d={`M ${FAN_W / 2} 0 C ${FAN_W / 2} 28, ${x} 24, ${x} ${FAN_H}`}
                />
              ))}
            </FanSvg>

            <PreviewGrid>
              {DESTINATIONS.map((dest, index) => {
                const Icon = dest.icon;
                return (
                  <Destination
                    key={dest.label}
                    $lit={fannedOut}
                    $delay={index * 110}
                  >
                    <DestinationMark $lit={fannedOut}>
                      <Icon size={20} strokeWidth={2} />
                    </DestinationMark>
                    <DestinationLabel>{dest.label}</DestinationLabel>
                  </Destination>
                );
              })}
            </PreviewGrid>
          </SceneLayer>

          {/* Scene 2: Optimize */}
          <SceneLayer $visible={step === 1}>
            <ChannelRail aria-label="Optimization channels">
              {CHANNELS.map((channel, index) => {
                const Icon = channel.icon;
                const lit = reduced || index === activeChannel;
                return (
                  <Channel key={channel.label} $lit={lit} $delay={index * 70}>
                    <Icon size={15} strokeWidth={2} />
                    {channel.label}
                  </Channel>
                );
              })}
            </ChannelRail>
            <OptimizeDemoStage>
              <OptimizeDemoLayer $active={activeChannel === 0}>
                <ArticleCard>
                  <ArticleTitle>The 8 Best Espresso Machines of 2026</ArticleTitle>
                  <ArticleByline>
                    Updated March 2026 · 14 min read
                  </ArticleByline>
                  {heatProducts.map((product) => (
                    <ProductRow key={product.name}>
                      <ProductThumb />
                      <ProductInfo>
                        <ProductName>{product.name}</ProductName>
                        <ProductBadge>{product.badge}</ProductBadge>
                      </ProductInfo>
                      <CheckButton>Check price</CheckButton>
                      <HeatBubble
                        $size={product.bubble.size}
                        $color={product.bubble.color}
                        $selected={product.bubble.ring && placementSelected}
                        $visible={bubblesVisible || reduced}
                      />
                    </ProductRow>
                  ))}
                  <StatsBar $visible={statsVisible || reduced}>
                    <StatsLink>
                      Check price <ExternalLink size={11} />
                    </StatsLink>
                    <StatItem $highlight="red">
                      <strong>123</strong> Clicks
                    </StatItem>
                    <StatItem>
                      <strong>$100.21</strong> from 1 sale
                    </StatItem>
                    <StatItem data-optional>
                      <strong>$0.81</strong> EPC
                    </StatItem>
                    <StatItem $highlight="green">
                      <strong>18.6%</strong> CTR
                    </StatItem>
                    <StatItem data-optional>
                      <strong>59%</strong> Seen
                    </StatItem>
                  </StatsBar>
                </ArticleCard>
              </OptimizeDemoLayer>

              <OptimizeDemoLayer $active={activeChannel === 1}>
                <OptimizeReportCard>
                  <ReportHeader>
                    <div>
                      <ReportTitle>Email earnings by section</ReportTitle>
                      <ReportSubtitle>
                        Revenue attributed to every newsletter placement
                      </ReportSubtitle>
                    </div>
                    <ReportTotal>
                      $18,420
                      <span>June commissions</span>
                    </ReportTotal>
                  </ReportHeader>
                  <EmailRows>
                    <EmailRow>
                      <EmailSection>
                        <strong>The Daily Deal</strong>
                        <span>Hero placement</span>
                      </EmailSection>
                      <EmailBarTrack>
                        <EmailBar $width={92} />
                      </EmailBarTrack>
                      <EmailAmount>$9,840</EmailAmount>
                    </EmailRow>
                    <EmailRow>
                      <EmailSection>
                        <strong>Editor’s Picks</strong>
                        <span>Product roundup</span>
                      </EmailSection>
                      <EmailBarTrack>
                        <EmailBar $width={61} />
                      </EmailBarTrack>
                      <EmailAmount>$5,210</EmailAmount>
                    </EmailRow>
                    <EmailRow>
                      <EmailSection>
                        <strong>Weekend Edition</strong>
                        <span>Deals section</span>
                      </EmailSection>
                      <EmailBarTrack>
                        <EmailBar $width={40} />
                      </EmailBarTrack>
                      <EmailAmount>$3,370</EmailAmount>
                    </EmailRow>
                  </EmailRows>
                </OptimizeReportCard>
              </OptimizeDemoLayer>

              <OptimizeDemoLayer $active={activeChannel === 2}>
                <OptimizeReportCard>
                  <ReportHeader>
                    <div>
                      <ReportTitle>Track every syndicated placement</ReportTitle>
                      <ReportSubtitle>
                        Attribute clicks and commissions by distribution platform
                      </ReportSubtitle>
                    </div>
                  </ReportHeader>
                  <PlatformGrid>
                    <Platform $color="#111827">Apple News</Platform>
                    <Platform $color="#6001d2">Yahoo!</Platform>
                    <Platform $color="#1490df">MSN</Platform>
                  </PlatformGrid>
                  <SyndicationNote>
                    See which stories, links, and partner platforms generate
                    affiliate revenue.
                  </SyndicationNote>
                </OptimizeReportCard>
              </OptimizeDemoLayer>

              <OptimizeDemoLayer $active={activeChannel === 3}>
                <OptimizeReportCard>
                  <VideoSummary>
                    <VideoThumbnail>
                      <PlayBadge>
                        <Play size={14} fill="currentColor" />
                      </PlayBadge>
                    </VideoThumbnail>
                    <VideoDetails>
                      <strong>The Best Espresso Setup for Every Budget</strong>
                      <span>Links in the description</span>
                      <Shortlink>publink.com/aF8eD59</Shortlink>
                    </VideoDetails>
                  </VideoSummary>
                  <CommissionReport>
                    <CommissionValue>
                      <span>Affiliate commissions from this video</span>
                      $4,286
                    </CommissionValue>
                    <CommissionBars aria-hidden>
                      {[32, 48, 41, 68, 56, 82, 100].map((height, index) => (
                        <CommissionBar
                          key={`${height}-${index}`}
                          $height={height}
                        />
                      ))}
                    </CommissionBars>
                  </CommissionReport>
                </OptimizeReportCard>
              </OptimizeDemoLayer>
            </OptimizeDemoStage>
          </SceneLayer>

          {/* Scene 3: Amplify */}
          <SceneLayer $visible={step === 2}>
            <AmplifyFlow>
              <AmplifyCard $active={!reviewReady} $complete={reviewReady}>
                <FlowKicker>
                  <Zap size={12} /> AI suggestion
                </FlowKicker>
                <FlowValue>+$1.2K/mo</FlowValue>
                <FlowCaption>
                  A higher-earning merchant opportunity across relevant coffee
                  product links.
                </FlowCaption>
                <FlowMeta>
                  <span>16 pages</span>
                  <span>Relevant products</span>
                </FlowMeta>
              </AmplifyCard>
              <FlowArrow $lit={reviewReady} aria-hidden>
                <ArrowRight size={18} />
              </FlowArrow>
              <AmplifyCard $active={reviewReady}>
                <FlowKicker>
                  <Check size={12} /> Review &amp; launch
                </FlowKicker>
                <FlowValue>Espresso Gear</FlowValue>
                <FlowCaption>
                  Review the suggested updates and launch when ready. Nothing
                  changes without your approval.
                </FlowCaption>
                <ReviewPill>Ready to review</ReviewPill>
              </AmplifyCard>
            </AmplifyFlow>
          </SceneLayer>
        </SceneStage>
      </StickyViewport>
    </ScrollStage>
  );
};
